import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { BoxPlanningCalendar } from "@/modules/boxPlanning/components/boxPlanningCalendar"
import { BoxPlanningHeader } from "@/modules/boxPlanning/components/boxPlanningHeader/boxPlanningHeader"
import { useBoxPrograms } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxProgrammationSidebar } from "@/modules/boxProgrammation/hooks/useBoxProgramSidebar"
import { cn } from "@/modules/lib/utils"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import BoxProgrammationEmpty from "@images/empty/programmations.svg"
import BoxPlanningVectors from "@images/svgIcons/boxPlanningVectors.svg"
import { CalendarIcon } from "@images/svgIcons/calendarIcon"
import { GroupIcon } from "@images/svgIcons/groupIcon"
import { PrivateIcon } from "@images/svgIcons/privateIcon"
import { ScoreboardIcon } from "@images/svgIcons/scoreboardIcon"
import { TimeTableIcon } from "@images/svgIcons/timeTableIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { Plus } from "lucide-react"
import { Suspense } from "react"

export function BoxPlanningContent() {
  const { data: boxProgrammations } = useBoxPrograms()
  const { navigateToBoxProgrammationCreation } = useBoxProgrammationSidebar()
  const canCreateActivity = useBoxStaffPermission(BoxStaffPermissions.CREATE_ACTIVITY)
  const canSeeTimetables = useBoxStaffPermission(BoxStaffPermissions.SEE_TIMETABLES)
  const { result: box } = useCurrentBox()
  const boxNavigate = useBoxNavigate()

  if (!canSeeTimetables && box?.currentUserRole === BoxStaffRole.FINANCIAL) {
    boxNavigate(Path.Box.Finances.Root)
  }

  return (
    <>
      {boxProgrammations.length === 0 ? (
        <div className="mx-auto flex grow flex-col justify-around gap-4 rounded-4xl bg-surface-foreground px-6 pt-2 pb-2 md:px-12 md:pt-4 md:pb-4 xl:mx-10 xl:max-w-7xl xl:px-24 xl:pt-8 xl:pb-8 2xl:mx-24 2xl:max-w-[90vw] 2xl:px-32 2xl:pt-12 2xl:pb-12">
          <div className="flex flex-col gap-2">
            <Text fontStyle="HL">{word("box.planning.empty.title")}</Text>
            <Text fontStyle="BM">{word("box.planning.empty.subtitle")}</Text>
          </div>

          <div className="flex flex-col-reverse items-center justify-center xl:flex-row">
            <div className="flex grow flex-col gap-6">
              <Text fontStyle="TM" className="mt-5 xl:mt-3">
                {word("box.planning.empty.possibilities")}
              </Text>
              <EmptyViewPossibility
                label={word("box.planning.empty.single_or_repeated")}
                icon={<CalendarIcon color="currentColor" className="text-textDefault-body" height={30} width={30} />}
              />
              <EmptyViewPossibility
                label={word("box.planning.empty.community")}
                icon={<GroupIcon color="currentColor" className="text-textDefault-body" height={30} width={30} />}
              />
              <EmptyViewPossibility
                label={word("box.planning.empty.limited_access")}
                icon={<PrivateIcon color="currentColor" className="text-textDefault-body" height={30} width={30} />}
              />
              <EmptyViewPossibility
                label={word("box.planning.empty.timetable")}
                icon={<TimeTableIcon color="currentColor" className="text-textDefault-body" height={30} width={30} />}
              />
              <EmptyViewPossibility
                label={word("box.planning.empty.scoreboard")}
                icon={<ScoreboardIcon color="currentColor" className="text-textDefault-body" height={30} width={30} />}
              />
            </div>
            <div className="grow">
              <img src={BoxProgrammationEmpty} width={400} height={300} alt="BoxProgrammationEmpty" />
            </div>
          </div>
          {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
          {canCreateActivity && (
            <Button
              className="mx-auto mt-4 w-fit lg:mt-8"
              size="lg"
              variant="primary"
              onClick={navigateToBoxProgrammationCreation}
            >
              <Plus color="currentColor" className="text-textDefault-body" height={30} width={30} />
              {word("box.planning.empty.create_activity")}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex h-full w-full min-w-280 flex-col gap-10 px-6 py-6 lg:mx-auto 2xl:w-8/10 2xl:px-0">
          <BoxPlanningHeader />
          <Suspense fallback={<LoadingSpinner />}>
            <BoxPlanningCalendar />
          </Suspense>
        </div>
      )}
    </>
  )
}

type EmptyViewPossibilityProps = {
  label: string
  icon: React.ReactElement
  className?: string
} & React.ComponentProps<"div">

function EmptyViewPossibility({ label, icon, className, ...props }: EmptyViewPossibilityProps) {
  return (
    <div className={cn("flex items-center gap-5", className)} {...props}>
      <div
        className="flex h-9.5 w-9 items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${BoxPlanningVectors})` }}
      >
        {icon}
      </div>
      <Text fontStyle="BM">{label}</Text>
    </div>
  )
}
